/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  ActivityIndicator,
  Alert,
  Dimensions,
  FlatList,
  Linking,
  RefreshControl,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import {
  MaterialCommunityIcons,
  Ionicons,
  FontAwesome5,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';

const { width, height } = Dimensions.get('window');

const FreeCoursesScreen = () => {
  const navigation = useNavigation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [freeCourses, setFreeCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [isOnline, setIsOnline] = useState(true);
  const [darkMode, setDarkMode] = useState(false);

  const platforms = [
    { id: 'all', label: 'All Platforms', icon: 'grid' },
    { id: 'coursera', label: 'Coursera', icon: 'school', color: '#0056D2' },
    { id: 'edx', label: 'edX', icon: 'book-open', color: '#00A2B1' },
    { id: 'mit', label: 'MIT OCW', icon: 'graduation-cap', color: '#A31F34' },
    { id: 'kaggle', label: 'Kaggle', icon: 'chart-line', color: '#20BEFF' },
    { id: 'nptel', label: 'NPTEL', icon: 'earth', color: '#FF6B35' },
  ];

  const levels = [
    { id: 'all', label: 'All Levels', icon: 'layers' },
    { id: 'beginner', label: 'Beginner', icon: 'school', color: '#10b981' },
    { id: 'intermediate', label: 'Intermediate', icon: 'trending-up', color: '#f59e0b' },
    { id: 'advanced', label: 'Advanced', icon: 'star', color: '#ef4444' },
  ];

  useEffect(() => {
    loadInitialData();
    setupNetInfoListener();
  }, []);

  useEffect(() => {
    filterCourses();
  }, [searchQuery, selectedPlatform, selectedLevel, freeCourses]);

  const setupNetInfoListener = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected);
    });
    return unsubscribe;
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadFreeCourses(),
        loadDarkModePreference(),
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      Alert.alert('Error', 'Failed to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadFreeCourses = async () => {
    try {
      const allFreeCourses = [
        // Mechanical Engineering
        {
          id: 'FREE_MECH1',
          title: 'Mechanical Engineering Design',
          subtitle: 'CAD, SolidWorks, Engineering Drawing',
          instructor: 'MIT OpenCourseWare',
          duration: '60 hours',
          rating: 4.9,
          students: 185400,
          level: 'intermediate',
          isFree: true,
          price: 0,
          color: '#FF6B35',
          icon: 'cog',
          category: 'mechanical',
          description: 'Complete mechanical engineering design course from MIT. Learn engineering graphics, SolidWorks basics, and advanced CAD techniques.',
          modules: [
            { id: '1', title: 'Engineering Graphics', duration: '15 hours', completed: false },
            { id: '2', title: 'SolidWorks Basics', duration: '20 hours', completed: false },
            { id: '3', title: 'Advanced CAD', duration: '25 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
          platform: 'mit',
          certificateAvailable: false,
          enrollmentLink: 'https://ocw.mit.edu/courses/2-007-design-and-manufacturing-i-spring-2009/',
          skills: ['CAD', 'SolidWorks', 'Engineering Drawing', '3D Modeling'],
          languages: ['English'],
          lastUpdated: '2024',
        },
        {
          id: 'FREE_MECH2',
          title: 'Thermodynamics Fundamentals',
          subtitle: 'Engineering thermodynamics for beginners',
          instructor: 'University of Michigan',
          duration: '40 hours',
          rating: 4.7,
          students: 124500,
          level: 'beginner',
          isFree: true,
          price: 0,
          color: '#FF6B35',
          icon: 'thermometer',
          category: 'mechanical',
          description: 'Learn the fundamentals of engineering thermodynamics.',
          modules: [
            { id: '1', title: 'Basic Concepts', duration: '10 hours', completed: false },
            { id: '2', title: 'First Law', duration: '15 hours', completed: false },
            { id: '3', title: 'Second Law', duration: '15 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
          platform: 'coursera',
          certificateAvailable: true,
          enrollmentLink: 'https://www.coursera.org/learn/thermodynamics-intro',
          skills: ['Thermodynamics', 'Heat Transfer', 'Energy Systems'],
          languages: ['English'],
          lastUpdated: '2024',
        },

        // Civil Engineering
        {
          id: 'FREE_CIVIL1',
          title: 'Structural Engineering Fundamentals',
          subtitle: 'Buildings, Bridges, and Infrastructure',
          instructor: 'University of Illinois via Coursera',
          duration: '35 hours',
          rating: 4.8,
          students: 156800,
          level: 'beginner',
          isFree: true,
          price: 0,
          color: '#0e4e96',
          icon: 'bridge',
          category: 'civil',
          description: 'Learn structural engineering principles for buildings and bridges.',
          modules: [
            { id: '1', title: 'Structural Analysis', duration: '12 hours', completed: false },
            { id: '2', title: 'Material Properties', duration: '10 hours', completed: false },
            { id: '3', title: 'Design Principles', duration: '13 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=400',
          platform: 'coursera',
          certificateAvailable: true,
          enrollmentLink: 'https://www.coursera.org/learn/structural-engineering',
          skills: ['Structural Analysis', 'CAD', 'Building Codes', 'Material Science'],
          languages: ['English'],
          lastUpdated: '2024',
        },
        {
          id: 'FREE_CIVIL2',
          title: 'Construction Project Management',
          subtitle: 'Manage construction projects effectively',
          instructor: 'Columbia University',
          duration: '30 hours',
          rating: 4.6,
          students: 89200,
          level: 'intermediate',
          isFree: true,
          price: 0,
          color: '#0e4e96',
          icon: 'hard-hat',
          category: 'civil',
          description: 'Learn project management techniques for construction projects.',
          modules: [
            { id: '1', title: 'Project Planning', duration: '10 hours', completed: false },
            { id: '2', title: 'Cost Estimation', duration: '10 hours', completed: false },
            { id: '3', title: 'Risk Management', duration: '10 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=400',
          platform: 'edx',
          certificateAvailable: true,
          enrollmentLink: 'https://www.edx.org/course/construction-project-management',
          skills: ['Project Management', 'Construction', 'Planning', 'Budgeting'],
          languages: ['English'],
          lastUpdated: '2024',
        },

        // Electrical Engineering
        {
          id: 'FREE_ELEC1',
          title: 'Electrical Circuits & Electronics',
          subtitle: 'Complete Electronics Engineering Course',
          instructor: 'MITx via edX',
          duration: '48 hours',
          rating: 4.9,
          students: 156800,
          level: 'intermediate',
          isFree: true,
          price: 0,
          color: '#f59e0b',
          icon: 'flash',
          category: 'electrical',
          description: 'Comprehensive electronics and circuit design course from MIT.',
          modules: [
            { id: '1', title: 'Circuit Theory', duration: '16 hours', completed: false },
            { id: '2', title: 'Digital Electronics', duration: '16 hours', completed: false },
            { id: '3', title: 'Power Systems', duration: '16 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1623479322729-28b25c16b011?w=400',
          platform: 'edx',
          certificateAvailable: true,
          enrollmentLink: 'https://www.edx.org/course/circuits-and-electronics-1-basic-circuit-analysi',
          skills: ['Circuit Design', 'Electronics', 'PCB Design', 'Power Systems'],
          languages: ['English'],
          lastUpdated: '2024',
        },
        {
          id: 'FREE_ELEC2',
          title: 'Renewable Energy Systems',
          subtitle: 'Solar, Wind, and Hydro Power',
          instructor: 'Stanford University',
          duration: '45 hours',
          rating: 4.8,
          students: 112300,
          level: 'intermediate',
          isFree: true,
          price: 0,
          color: '#f59e0b',
          icon: 'solar-power',
          category: 'electrical',
          description: 'Learn about renewable energy systems and sustainable power generation.',
          modules: [
            { id: '1', title: 'Solar Energy', duration: '15 hours', completed: false },
            { id: '2', title: 'Wind Power', duration: '15 hours', completed: false },
            { id: '3', title: 'Hydro Systems', duration: '15 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=400',
          platform: 'coursera',
          certificateAvailable: true,
          enrollmentLink: 'https://www.coursera.org/learn/renewable-energy',
          skills: ['Renewable Energy', 'Solar Power', 'Wind Energy', 'Sustainability'],
          languages: ['English'],
          lastUpdated: '2024',
        },

        // Computer Engineering
        {
          id: 'FREE_COMP1',
          title: 'Python for Engineering Applications',
          subtitle: 'Scientific Computing with Python',
          instructor: 'Kaggle',
          duration: '30 hours',
          rating: 4.7,
          students: 289300,
          level: 'beginner',
          isFree: true,
          price: 0,
          color: '#3776AB',
          icon: 'language-python',
          category: 'computer',
          description: 'Python programming for engineering calculations and simulations.',
          modules: [
            { id: '1', title: 'Python Basics', duration: '10 hours', completed: false },
            { id: '2', title: 'NumPy & SciPy', duration: '10 hours', completed: false },
            { id: '3', title: 'Engineering Applications', duration: '10 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1526379879527-8559ecfcaec7?w=400',
          platform: 'kaggle',
          certificateAvailable: true,
          enrollmentLink: 'https://www.kaggle.com/learn/python',
          skills: ['Python', 'NumPy', 'SciPy', 'Data Analysis', 'Simulation'],
          languages: ['English'],
          lastUpdated: '2024',
        },
        {
          id: 'FREE_COMP2',
          title: 'Machine Learning for Engineers',
          subtitle: 'AI applications in engineering systems',
          instructor: 'Google',
          duration: '40 hours',
          rating: 4.8,
          students: 215600,
          level: 'intermediate',
          isFree: true,
          price: 0,
          color: '#FF4081',
          icon: 'robot',
          category: 'computer',
          description: 'Apply machine learning techniques to solve complex engineering problems.',
          modules: [
            { id: '1', title: 'ML Fundamentals', duration: '12 hours', completed: false },
            { id: '2', title: 'Engineering Datasets', duration: '14 hours', completed: false },
            { id: '3', title: 'Real Applications', duration: '14 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400',
          platform: 'coursera',
          certificateAvailable: true,
          enrollmentLink: 'https://www.coursera.org/learn/machine-learning',
          skills: ['Machine Learning', 'Python', 'Data Science', 'AI'],
          languages: ['English'],
          lastUpdated: '2024',
        },

        // Chemical Engineering
        {
          id: 'FREE_CHEM1',
          title: 'Chemical Process Engineering',
          subtitle: 'Process Design & Optimization',
          instructor: 'NPTEL - IIT Bombay',
          duration: '40 hours',
          rating: 4.6,
          students: 87400,
          level: 'advanced',
          isFree: true,
          price: 0,
          color: '#10b981',
          icon: 'flask',
          category: 'chemical',
          description: 'Chemical engineering process design and optimization.',
          modules: [
            { id: '1', title: 'Process Design', duration: '15 hours', completed: false },
            { id: '2', title: 'Reaction Engineering', duration: '15 hours', completed: false },
            { id: '3', title: 'Process Control', duration: '10 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=400',
          platform: 'nptel',
          certificateAvailable: true,
          enrollmentLink: 'https://nptel.ac.in/courses/103/101/103101087/',
          skills: ['Process Design', 'Chemical Reactions', 'Optimization', 'Control Systems'],
          languages: ['English'],
          lastUpdated: '2024',
        },

        // More Courses
        {
          id: 'FREE_COMP3',
          title: 'Data Structures & Algorithms',
          subtitle: 'For Engineering Problem Solving',
          instructor: 'Princeton University',
          duration: '50 hours',
          rating: 4.9,
          students: 325000,
          level: 'intermediate',
          isFree: true,
          price: 0,
          color: '#805ad5',
          icon: 'code-braces',
          category: 'computer',
          description: 'Essential data structures and algorithms for engineering applications.',
          modules: [
            { id: '1', title: 'Basic Structures', duration: '15 hours', completed: false },
            { id: '2', title: 'Advanced Algorithms', duration: '20 hours', completed: false },
            { id: '3', title: 'Engineering Problems', duration: '15 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400',
          platform: 'coursera',
          certificateAvailable: true,
          enrollmentLink: 'https://www.coursera.org/learn/algorithms-part1',
          skills: ['Algorithms', 'Data Structures', 'Problem Solving', 'Java'],
          languages: ['English'],
          lastUpdated: '2024',
        },
        {
          id: 'FREE_MECH3',
          title: 'Robotics Fundamentals',
          subtitle: 'Introduction to Robotics Engineering',
          instructor: 'University of Pennsylvania',
          duration: '35 hours',
          rating: 4.7,
          students: 128400,
          level: 'beginner',
          isFree: true,
          price: 0,
          color: '#667eea',
          icon: 'robot-industrial',
          category: 'mechanical',
          description: 'Introduction to robotics engineering principles and applications.',
          modules: [
            { id: '1', title: 'Robotics Basics', duration: '10 hours', completed: false },
            { id: '2', title: 'Control Systems', duration: '15 hours', completed: false },
            { id: '3', title: 'Practical Applications', duration: '10 hours', completed: false },
          ],
          videoPreview: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
          thumbnail: 'https://images.unsplash.com/photo-1589256469067-ea99122bbdc4?w=400',
          platform: 'coursera',
          certificateAvailable: true,
          enrollmentLink: 'https://www.coursera.org/learn/robotics',
          skills: ['Robotics', 'Control Systems', 'Automation', 'Programming'],
          languages: ['English'],
          lastUpdated: '2024',
        },
      ];

      setFreeCourses(allFreeCourses);
      setFilteredCourses(allFreeCourses);
    } catch (error) {
      console.error('Error loading free courses:', error);
      throw error;
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const filterCourses = () => {
    let filtered = freeCourses;
    
    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(course =>
        String(course.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(course.subtitle ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(course.instructor ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(course.category ?? '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Filter by platform
    if (selectedPlatform !== 'all') {
      filtered = filtered.filter(course => course.platform === selectedPlatform);
    }
    
    // Filter by level
    if (selectedLevel !== 'all') {
      filtered = filtered.filter(course => course.level === selectedLevel);
    }
    
    setFilteredCourses(filtered);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadFreeCourses();
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const openCourseLink = async (course) => {
    try {
      if (!isOnline) {
        Alert.alert('Offline', 'You need to be online to access this course.');
        return;
      }
      
      await Linking.openURL(course.enrollmentLink);
    } catch (error) {
      console.error('Error opening course link:', error);
      Alert.alert('Error', 'Failed to open course link.');
    }
  };

  const getPlatformIcon = (platformId) => {
    const platform = platforms.find(p => p.id === platformId);
    return platform ? platform.icon : 'earth';
  };

  const getLevelColor = (level) => {
    switch(level) {
      case 'beginner': return '#10b981';
      case 'intermediate': return '#f59e0b';
      case 'advanced': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const renderCourseCard = ({ item }) => (
    <TouchableOpacity
      style={styles.courseCard}
      onPress={() => openCourseLink(item)}
      activeOpacity={0.8}
    >
      <View style={styles.courseHeader}>
        <LinearGradient
          colors={[item.color, `${item.color}80`]}
          style={styles.courseIconContainer}
        >
          <MaterialCommunityIcons name={item.icon} size={28} color="#ffffff" />
        </LinearGradient>
        <View style={styles.courseHeaderInfo}>
          <View style={styles.courseTitleRow}>
            <Text style={styles.courseTitle} numberOfLines={2}>{item.title}</Text>
            <View style={styles.freeBadge}>
              <Text style={styles.freeBadgeText}>FREE</Text>
            </View>
          </View>
          <Text style={styles.courseSubtitle} numberOfLines={2}>{item.subtitle}</Text>
          <Text style={styles.courseInstructor}>{item.instructor}</Text>
        </View>
      </View>
      
      <View style={styles.courseDetails}>
        <View style={styles.detailRow}>
          <View style={styles.detailItem}>
            <MaterialCommunityIcons name={getPlatformIcon(item.platform)} size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.detailText}>
              {platforms.find(p => p.id === item.platform)?.label || item.platform}
            </Text>
          </View>
          <View style={styles.detailItem}>
            <MaterialCommunityIcons name="clock-outline" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.detailText}>{item.duration}</Text>
          </View>
        </View>
        
        <View style={styles.detailRow}>
          <View style={styles.detailItem}>
            <MaterialCommunityIcons name="star" size={16} color="#fbbf24" />
            <Text style={styles.detailText}>{item.rating}</Text>
            <Text style={styles.detailSubtext}>({item.students.toLocaleString()})</Text>
          </View>
          <View style={styles.detailItem}>
            <View style={[styles.levelBadge, { backgroundColor: getLevelColor(item.level) + '20' }]}>
              <Text style={[styles.levelText, { color: getLevelColor(item.level) }]}>
                {item.level.charAt(0).toUpperCase() + item.level.slice(1)}
              </Text>
            </View>
          </View>
        </View>
      </View>
      
      <Text style={styles.courseDescription} numberOfLines={3}>{item.description}</Text>
      
      <View style={styles.skillsContainer}>
        {item.skills.slice(0, 3).map((skill, index) => (
          <View key={index} style={styles.skillTag}>
            <Text style={styles.skillText}>{skill}</Text>
          </View>
        ))}
        {item.skills.length > 3 && (
          <View style={styles.moreSkillsTag}>
            <Text style={styles.moreSkillsText}>+{item.skills.length - 3}</Text>
          </View>
        )}
      </View>
      
      <TouchableOpacity 
        style={styles.enrollButton}
        onPress={() => openCourseLink(item)}
      >
        <MaterialCommunityIcons name="open-in-new" size={18} color="#ffffff" />
        <Text style={styles.enrollButtonText}>Enroll Now</Text>
      </TouchableOpacity>
    </TouchableOpacity>
  );

  const renderPlatformFilter = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false} 
      style={styles.filterContainer}
    >
      {platforms.map((platform) => (
        <TouchableOpacity
          key={platform.id}
          style={[
            styles.filterButton,
            selectedPlatform === platform.id && styles.filterButtonActive,
            selectedPlatform === platform.id && platform.color && { backgroundColor: platform.color },
          ]}
          onPress={() => setSelectedPlatform(platform.id)}
        >
          <MaterialCommunityIcons 
            name={platform.icon} 
            size={16} 
            color={selectedPlatform === platform.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#6b7280')} 
          />
          <Text style={[
            styles.filterText,
            selectedPlatform === platform.id && styles.filterTextActive,
          ]}>
            {platform.label}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderLevelFilter = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false} 
      style={styles.filterContainer}
    >
      {levels.map((level) => (
        <TouchableOpacity
          key={level.id}
          style={[
            styles.filterButton,
            selectedLevel === level.id && styles.filterButtonActive,
            selectedLevel === level.id && level.color && { backgroundColor: level.color },
          ]}
          onPress={() => setSelectedLevel(level.id)}
        >
          <MaterialCommunityIcons 
            name={level.icon} 
            size={16} 
            color={selectedLevel === level.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#6b7280')} 
          />
          <Text style={[
            styles.filterText,
            selectedLevel === level.id && styles.filterTextActive,
          ]}>
            {level.label}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <TouchableOpacity style={styles.searchIcon}>
        <MaterialCommunityIcons name="magnify" size={24} color={darkMode ? "#cbd5e0" : "#6b7280"} />
      </TouchableOpacity>
      <TextInput
        style={styles.searchInput}
        placeholder="Search free courses..."
        value={searchQuery}
        onChangeText={setSearchQuery}
        placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
      />
      {searchQuery.length > 0 && (
        <TouchableOpacity style={styles.clearButton} onPress={() => setSearchQuery('')}>
          <MaterialCommunityIcons name="close-circle" size={20} color={darkMode ? "#cbd5e0" : "#6b7280"} />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons name="book-open-variant" size={64} color={darkMode ? "#4a5568" : "#d1d5db"} />
      <Text style={styles.emptyStateTitle}>No courses found</Text>
      <Text style={styles.emptyStateText}>
        {searchQuery ? 'Try a different search term' : 'Try changing your filters'}
      </Text>
      <TouchableOpacity 
        style={styles.resetButton}
        onPress={() => {
          setSearchQuery('');
          setSelectedPlatform('all');
          setSelectedLevel('all');
        }}
      >
        <Text style={styles.resetButtonText}>Reset Filters</Text>
      </TouchableOpacity>
    </View>
  );

  const renderHeader = () => (
    <LinearGradient
      colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
      style={styles.headerGradient}
    >
      <View style={styles.headerContent}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
        </TouchableOpacity>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>🎓 Free Courses</Text>
          <Text style={styles.headerSubtitle}>Premium education at no cost</Text>
        </View>
        <View style={styles.headerStats}>
          <Text style={styles.courseCount}>{freeCourses.length} Courses</Text>
          <Text style={styles.courseInfo}>All 100% Free</Text>
        </View>
      </View>
    </LinearGradient>
  );

  const gradients = darkMode ? {
    primary: ['#2d3748', '#4a5568'],
    success: ['#38a169', '#68d391'],
  } : {
    primary: ['#667eea', '#764ba2'],
    success: ['#4facfe', '#00f2fe'],
  };

  const styles = getStyles(darkMode);

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#667eea" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#667eea" />
          <Text style={styles.loadingText}>Loading free courses...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      
      {renderHeader()}
      
      <ScrollView 
        style={styles.container}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
      >
        {renderSearchBar()}
        
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Filter by Platform</Text>
          {renderPlatformFilter()}
        </View>
        
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Filter by Level</Text>
          {renderLevelFilter()}
        </View>
        
        <View style={styles.section}>
          <View style={styles.resultsHeader}>
            <Text style={styles.sectionTitle}>
              {filteredCourses.length} Free Courses Available
            </Text>
            <Text style={styles.resultsSubtitle}>
              All courses are completely free with certificates
            </Text>
          </View>
          
          {filteredCourses.length > 0 ? (
            <FlatList
              data={filteredCourses}
              renderItem={renderCourseCard}
              keyExtractor={(item) => item.id}
              scrollEnabled={false}
              contentContainerStyle={styles.coursesList}
              showsVerticalScrollIndicator={false}
            />
          ) : (
            renderEmptyState()
          )}
        </View>
        
        <View style={styles.infoSection}>
          <MaterialCommunityIcons name="information" size={24} color="#667eea" />
          <Text style={styles.infoText}>
            All courses are from reputable platforms and include certificates. 
            Some may require payment for verified certificates.
          </Text>
        </View>
        
        <View style={styles.bottomPadding} />
      </ScrollView>
      
      {!isOnline && (
        <View style={styles.offlineBanner}>
          <MaterialCommunityIcons name="wifi-off" size={16} color="#ffffff" />
          <Text style={styles.offlineText}>Offline - Courses require internet</Text>
        </View>
      )}
    </SafeAreaView>
  );
};

const getStyles = (darkMode) => StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  headerGradient: {
    paddingHorizontal: 20,
    paddingTop: Platform.OS === 'ios' ? 20 : 40,
    paddingBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
  },
  headerTitleContainer: {
    flex: 1,
    marginLeft: 12,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#ffffff',
    opacity: 0.9,
    marginTop: 4,
  },
  headerStats: {
    alignItems: 'flex-end',
  },
  courseCount: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  courseInfo: {
    fontSize: 12,
    color: '#ffffff',
    opacity: 0.9,
    marginTop: 2,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginHorizontal: 20,
    marginVertical: 16,
    borderRadius: 12,
    paddingHorizontal: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  searchIcon: {
    paddingRight: 12,
  },
  searchInput: {
    flex: 1,
    height: 48,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  clearButton: {
    padding: 4,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginHorizontal: 20,
    marginBottom: 12,
  },
  filterContainer: {
    paddingHorizontal: 20,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  filterButtonActive: {
    borderColor: 'transparent',
  },
  filterText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  filterTextActive: {
    color: '#ffffff',
  },
  resultsHeader: {
    marginHorizontal: 20,
    marginBottom: 16,
  },
  resultsSubtitle: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginTop: 4,
  },
  coursesList: {
    paddingHorizontal: 20,
  },
  courseCard: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  courseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  courseIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  courseHeaderInfo: {
    flex: 1,
  },
  courseTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  courseTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginRight: 8,
  },
  freeBadge: {
    backgroundColor: '#10b981',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  freeBadgeText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  courseSubtitle: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginBottom: 4,
  },
  courseInstructor: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    fontWeight: '500',
  },
  courseDetails: {
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailText: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginLeft: 6,
  },
  detailSubtext: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 4,
  },
  levelBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  levelText: {
    fontSize: 12,
    fontWeight: '600',
  },
  courseDescription: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    lineHeight: 20,
    marginBottom: 12,
  },
  skillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  skillTag: {
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    marginRight: 8,
    marginBottom: 8,
  },
  skillText: {
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    fontWeight: '500',
  },
  moreSkillsTag: {
    backgroundColor: darkMode ? '#2d3748' : '#e5e7eb',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    marginBottom: 8,
  },
  moreSkillsText: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  enrollButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#667eea',
    paddingVertical: 12,
    borderRadius: 8,
  },
  enrollButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    paddingHorizontal: 32,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 16,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
    marginBottom: 24,
  },
  resetButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  resetButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  infoSection: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginHorizontal: 20,
    marginBottom: 24,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginLeft: 12,
    lineHeight: 20,
  },
  bottomPadding: {
    height: 80,
  },
  offlineBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#6b7280',
    paddingVertical: 8,
    paddingHorizontal: 16,
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  offlineText: {
    color: '#ffffff',
    marginLeft: 8,
    fontSize: 14,
  },
});

export default FreeCoursesScreen;